import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '@/components/MobileLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ChevronLeft, Users, Globe, Trash2, Loader2, Shield, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Accès refusé');
      nav('/');
    }
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/media'),
      ]);
      setUsers(u.data); setMedias(m.data);
    } catch (e) {
      toast.error('Chargement impossible');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Supprimer définitivement ${email} et toutes ses données ?`)) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success(`Utilisateur ${email} supprimé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const deleteMedia = async (mid, name) => {
    if (!window.confirm(`Supprimer le média "${name}" ?`)) return;
    try {
      await api.delete(`/admin/media/${mid}`);
      toast.success('Média supprimé');
      await load();
    } catch (e) { toast.error('Échec'); }
  };

  if (!user?.is_admin) return null;

  return (
    <MobileLayout>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav('/')} data-testid="btn-back" className="flex items-center gap-1 text-slate-700 font-medium">
          <ChevronLeft size={22} /> Retour
        </button>
        <h1 className="font-bold text-slate-900 flex items-center gap-1.5">
          <Shield size={16} className="text-purple-600" /> Admin
        </h1>
        <div className="w-8" />
      </header>

      <div className="px-5 pt-4 flex gap-2" data-testid="admin-tabs">
        <button
          onClick={() => setTab('users')}
          data-testid="tab-users"
          className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 transition ${tab === 'users' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          <Users size={16} /> Utilisateurs ({users.length})
        </button>
        <button
          onClick={() => setTab('medias')}
          data-testid="tab-medias"
          className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 transition ${tab === 'medias' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}
        >
          <Globe size={16} /> Médias ({medias.length})
        </button>
      </div>

      <div className="px-5 pt-4 pb-8 space-y-3">
        {loading && <Loader2 className="mx-auto animate-spin text-purple-600" />}

        {!loading && tab === 'users' && users.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-user-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900 truncate">{u.name || u.email}</span>
                  {u.is_admin && <span className="text-[10px] uppercase font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Admin</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                <div className="flex gap-3 mt-2 text-xs text-slate-600">
                  <span>📝 {u.article_count} articles</span>
                  <span>🌐 {u.media_count} médias</span>
                </div>
              </div>
              {!u.is_admin && (
                <button
                  onClick={() => deleteUser(u.id, u.email)}
                  data-testid={`btn-delete-user-${u.id}`}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                  aria-label="Supprimer"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}

        {!loading && tab === 'users' && users.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-8">Aucun utilisateur.</p>
        )}

        {!loading && tab === 'medias' && medias.map((m) => (
          <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`admin-media-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                <a href={m.url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline truncate flex items-center gap-1">
                  {m.url} <ExternalLink size={10} />
                </a>
                <div className="text-xs text-slate-500 mt-1">
                  Propriétaire : <span className="font-medium text-slate-700">{m.owner_email || '—'}</span>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">@{m.username}</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                    {m.default_status === 'publish' ? 'Publier' : 'Brouillon'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => deleteMedia(m.id, m.name)}
                data-testid={`btn-delete-media-${m.id}`}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                aria-label="Supprimer"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}

        {!loading && tab === 'medias' && medias.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-8">Aucun média configuré.</p>
        )}
      </div>
    </MobileLayout>
  );
}
